'use client';
import React, { useRef } from 'react';
import CommentForm, { type CommentFormRef } from '@/screen/feed/view/components/comment-form';
import CommentList from '@/screen/feed/view/components/comment-list';
import { useComments } from '@/screen/feed/view/hooks/useComments';
import { simplifyNumber } from '@/libs/utils/number';
import styles from './styles.module.scss';

interface CommentPanelProps {
  feedId: string;
  commentCount?: number;
  className?: string;
}

/**
 * PC 피드 상세의 댓글 패널(lone-web-pc pc-v2-a-detail). 1024px 이상은 본문 오른쪽, 그보다 좁으면 본문 아래에 놓인다.
 * 모바일은 기존 댓글 시트를 쓴다.
 */
const CommentPanel = ({ feedId, commentCount = 0, className = '' }: CommentPanelProps) => {
  const commentFormRef = useRef<CommentFormRef>(null);
  const {
    comments,
    isCommentsLoading,
    isLoadingMore,
    hasMoreComments,
    newComment,
    isCommentSubmitting,
    editingComment,
    onChangeComment,
    onSubmitComment,
    onEdit,
    onDelete,
    onCancelEdit,
    onUpdateComment,
    onChangeEditingComment,
    onLoadMoreComments,
  } = useComments(feedId, () => commentFormRef.current?.clear());

  return (
    <section id="comments" className={`${styles.panel} ${className}`} aria-labelledby="comment-panel-title">
      <h2 id="comment-panel-title" className={styles.title}>
        댓글 {simplifyNumber(commentCount)}
      </h2>
      <div className={styles.list}>
        <CommentList
          comments={comments}
          isCommentsLoading={isCommentsLoading}
          isLoadingMore={isLoadingMore}
          hasMoreComments={hasMoreComments}
          editingComment={editingComment}
          isCommentSubmitting={isCommentSubmitting}
          onEdit={onEdit}
          onDelete={onDelete}
          onCancelEdit={onCancelEdit}
          onUpdateComment={onUpdateComment}
          onChangeEditingComment={onChangeEditingComment}
          onLoadMoreComments={onLoadMoreComments}
        />
      </div>
      <div className={styles.form}>
        <CommentForm
          ref={commentFormRef}
          variant="panel"
          newComment={newComment}
          isCommentSubmitting={isCommentSubmitting}
          onChangeComment={onChangeComment}
          onSubmitComment={onSubmitComment}
        />
      </div>
    </section>
  );
};

export default CommentPanel;
